import { TBeeEditor } from "./types"
import "./styles.scss"
import { Box, Stack, Tooltip, Typography } from "@mui/material"
import {
    getBeeTokenService,
    LoadingContainer,
    GenericWrapperContext,
    Modal,
    getBrandUrl,
    Button,
    Icon,
    Dropdown,
    fetchRoute,
    DialogHandler,
    EEditorType
} from "@cakemail-org/ui-components-v2"
import { useContext, useEffect, useRef, useState } from "react"
import { logError } from "../../utils/data"
import Bee from '@beefree.io/sdk'
import { IEntityContentJson, IToken } from "@beefree.io/sdk/dist/types/bee"
import { getInitialBeeConfiguration } from "./config/config"
import emptyBeeTemplate from "./baseContent/emptyContent"
import { useTranslation } from "react-i18next"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { ERoutes } from "../../utils/url"
import { ELogErrorMsg } from "../../utils/data/types"
import { showUnsaveChangesDialog } from "../dialogs/unsaveChanges"
import { TUnsaveChangesDialogResult } from "../dialogs/unsaveChanges/types"
import { getBaseForm } from "./baseContent/baseForm"
import { getBasePopup } from "./baseContent/basePopup"

export function BeeEditor({
    variant = "modal",
    hidden,
    titleContent,
    className,
    editorType,
    initContent,
    contentContext,
    containerName,
    onClose,
    onSave,
    onChange,
    onSaveAsTemplate,
    onAutoSave,
    onSend,
    onLoad,
    onError,
    onWarning,
    onBrandingToggle,
    onFilePickerCancel,
    onFilePickerInsert
}: TBeeEditor) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const [loading, setLoading] = useState(editorType === EEditorType.fileManager ? false : true)
    const beeInstance = useRef<Bee>(null)

    const hasChangesToSave = useRef(false)
    // Use ref to store current contentContext - this bypasses closure issues
    const localContent = useRef<any>(initContent || {})
    const contentContextRef = useRef(contentContext)
    contentContextRef.current = contentContext

    useEffect(() => {
        initializeBeeEditor()
    }, [])

    function initializeBeeEditor() {
        getBeeTokenService({
            editorType
        }).then((token: IToken) => {
            setBeeInstance(token, initContent)
        }).catch((error) => {
            logError(ELogErrorMsg.ErrorGettingBeeToken, error)
        })
    }

    function getBeeConfig(reload = false) {
        return getInitialBeeConfiguration({
            reload,
            containerName: containerName || `beeEditorContainer-${editorType}`,
            account: genericWrapper.account,
            user: genericWrapper.user,
            partnerBrand: genericWrapper.partnerBrand,
            userBrand: genericWrapper.userBrand,
            editorType,
            getContentContext: () => contentContextRef.current || {},
            getLocalContent: () => localContent.current,
            onChange: handleOnBeeChange,
            onSaveAsTemplate: handleOnBeeSaveAsTemplate,
            onAutoSave: handleOnBeeAutoSave,
            onSend: handleOnBeeSend,
            onLoad: handleOnBeeLoad,
            onError: handleOnBeeError,
            onWarning: handleOnBeeWarning,
            onFilePickerInsert,
            onFilePickerCancel
        })
    }

    function setBeeInstance(token: IToken, content: IEntityContentJson) {
        // is this still needed? const { cleanJSON, cleanHTML } = parseEmailContent(content);
        let curContent = content
        if (!curContent && editorType === EEditorType.formDesigner) {
            curContent = getBaseForm()
        } else if (!curContent && editorType === EEditorType.emailDesigner) {
            curContent = emptyBeeTemplate
        } else if (!curContent && editorType === EEditorType.popUpDesigner) {
            curContent = getBasePopup()
        }

        beeInstance.current = new Bee(token)
        beeInstance.current.start(
            getBeeConfig(),
            curContent
        )
    }

    function handleManageBrand() {
        window.location.href = "#" + fetchRoute({
            route: ERoutes.brand,
        })
    }

    function handleBrandingToggle() {
        onBrandingToggle && onBrandingToggle().then((content: any) => {
            DialogHandler.show({
                type: "success",
                title: t(contentContext?.isSmart ? "general-branding-disabled" : "general-branding-enabled"),
            })
            reloadBeeEditor()
        })
            .catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorOnBrandingToggle, error)
            })
    }

    async function reloadBeeEditor(newContent?: any) {
        try {
            return await beeInstance.current?.reload(
                newContent || localContent.current || initContent, getBeeConfig(true))
        } catch (error: any) {
            logError(ELogErrorMsg.ErrorReloadingBeeEditor, error)
        }
    }

    function closeEditor() {
        onClose && onClose()
    }

    function handleOnClose() {
        if (hasChangesToSave.current) {
            showUnsaveChangesDialog()
                .then(({ e, action, btnRef }: TUnsaveChangesDialogResult) => {
                    if (action === "save") {
                        return handleOnBeeSave(e, btnRef).then(() => {
                            DialogHandler.hide()
                            closeEditor()
                        })
                    } else if (action === "close") {
                        closeEditor()
                    }
                    return
                })
            return
        }
        closeEditor()
    }

    /*
    ==========
    Bee Functions to handle data treatment
    ==========
    */
    async function handleOnBeeSave(e: React.MouseEvent<HTMLButtonElement>, btn?: TButtonRef) {
        return beeInstance.current?.save().then((response: any) => {
            const { json, html } = response.data
            if (onSave) {
                return onSave(json, html)
            }
        }).then(() => {
            hasChangesToSave.current = false
        }).catch(() => {
            DialogHandler.show({
                type: "error",
                title: t("general-error-action"),
            })
        }).finally(() => {
            btn && btn.reset()
        })
    }

    function handleOnBeeChange(jsonFile: string, response: any) {
        localContent.current = jsonFile
        hasChangesToSave.current = true
        onChange && onChange(jsonFile, response)
    }
    function handleOnBeeSaveAsTemplate(jsonFile: string) {
        console.log("onSaveAsTemplate", jsonFile)
        onSaveAsTemplate && onSaveAsTemplate(jsonFile)
    }
    function handleOnBeeAutoSave(jsonFile: string) {
        console.log("onAutoSave", jsonFile)
        onAutoSave && onAutoSave(jsonFile)
    }
    function handleOnBeeSend(htmlFile: string) {
        console.log("onSend", htmlFile)
        onSend && onSend(htmlFile)
    }
    function handleOnBeeLoad(jsonFile: string) {
        setLoading(false)
        onLoad && onLoad(jsonFile)
    }
    function handleOnBeeError(errorMessage: string) {
        console.log("onError", errorMessage)
        onError && onError(errorMessage)
    }
    function handleOnBeeWarning(alertMessage: string) {
        console.log("onWarning", alertMessage)
        onWarning && onWarning(alertMessage)
    }

    const ddActions: TDropMenuItem[] = []

    /*
    // TODO: Add send test email
    // Chain bee save with save or save then send test email
    if(onSendTestEmail){
        ddActions.push({
            text: t("general-send-test-email"),
            onClick: handleOnBeeSendTest
        })
    }*/

    if (onBrandingToggle) {
        ddActions.push({
            isAction: true,
            text: t(contentContext?.isSmart ? "general-disable-branding" : "general-enable-branding"),
            onClick: handleBrandingToggle
        })
    }

    ddActions.push({
        isAction: true,
        text: t("general-manage-brand"),
        onClick: handleManageBrand,
        postContent: <Icon name="Edit18" />
    })



    return <>
        {variant === "modal" &&
            <Modal
                open={true}
                className={`beeEditorModal ${hidden ? "hidden" : ""} ${className || ""}`}
                heading={{
                    logoUrl: getBrandUrl({ brand: genericWrapper.partnerBrand, elKey: "logo" }),
                    titleContent: titleContent,
                    actions: [
                        ...(contentContext?.isSmart ? [<Tooltip title={t("smart-resource-desc")} placement="bottom">
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography className="dashed">{t("general-smart")}</Typography>
                                <Icon name="Info18" color="warning" />
                            </Stack>
                        </Tooltip>] : []),
                        <Button key="save" animated color="secondary" variant='outlined' onClick={handleOnBeeSave} disabled={loading} >
                            {t("general-save")}
                        </Button>,
                        <Dropdown
                            value={""}
                            key="ddActions"
                            name="ddActions"
                            color="secondary"
                            iconOnly
                            disabled={loading}
                            options={ddActions}
                        />,
                        <Button key="close" color="shade150" onClick={handleOnClose} >{t("general-close")}</Button>
                    ]
                }}
                content={<>
                    <Box id={containerName || `beeEditorContainer-${editorType}`} className={`beeEditorContainer ${className || ""} ${loading ? "hidden" : ""}`}>

                    </Box>
                    <LoadingContainer loading={loading}></LoadingContainer>
                </>
                }
            />
        }

        {variant === "inline" &&
            <Stack className={`beeEditorModal ${hidden ? "hidden" : ""} ${className || ""}`}>
                <Box id={containerName || `beeEditorContainer-${editorType}`} className={`beeEditorContainer ${className || ""} ${loading ? "hidden" : ""}`} flex={1}>

                </Box>
                <LoadingContainer loading={loading}></LoadingContainer>
            </Stack >
        }
    </>
}

export default BeeEditor